import type { Task, TaskActivity, TaskStatus, User } from '../../types';

export type ParticipantRole = 'writers' | 'reviewers';
export interface ParticipantTask {
  task: Task;
  reviewMs: number;
  progressMs: number;
  sinceLastActivityMs: number | null;
  revisions: number;
  approved: boolean;
}
export interface ParticipantRow {
  id: string;
  name: string;
  email: string;
  expertId: string;
  trusted?: boolean;
  tasks: ParticipantTask[];
  signedOff: number;
  inReview: number;
  averageReviewMs: number | null;
  averageWritingMs: number | null;
  revisionRatio: number | null;
}

export function canReviewCampaign(user: User, campaignId: string): boolean {
  return (
    user.isAdmin === true ||
    (user.isReviewer === true && (user.reviewCampaignIds ?? []).includes(campaignId))
  );
}

/** Sum every recorded state interval, including the open interval up to now.
 * Snapshot timestamps recover the latest cycle for legacy tasks without history. */
export function taskDurations(task: Task, events: TaskActivity[], now: number) {
  const transitions = events
    .filter((event) => event.toStatus && event.toStatus !== event.fromStatus)
    .map((event) => ({ status: event.toStatus!, at: event.createdAt.getTime() }));
  const recover = (status: TaskStatus, at: Date | null | undefined) => {
    if (at && !transitions.some((entry) => entry.at === at.getTime())) {
      transitions.push({ status, at: at.getTime() });
    }
  };
  // These timestamps identify the latest cycle, not every historical cycle.
  recover('claimed', task.claimedAt);
  recover('in_review', task.submittedAt);
  if (task.status === 'approved' || task.status === 'changes_requested') {
    recover(task.status, task.reviewedAt);
  }
  transitions.sort((a, b) => a.at - b.at);
  const last = transitions[transitions.length - 1];
  if (last && last.status !== task.status && task.updatedAt?.getTime() >= last.at) {
    recover(task.status, task.updatedAt);
    transitions.sort((a, b) => a.at - b.at);
  }
  let reviewMs = 0;
  let progressMs = 0;
  transitions.forEach((entry, index) => {
    const end = transitions[index + 1]?.at ?? now;
    const duration = Math.max(0, Math.min(end, now) - entry.at);
    if (entry.status === 'in_review') reviewMs += duration;
    if (['claimed', 'in_progress', 'changes_requested'].includes(entry.status)) {
      progressMs += duration;
    }
  });
  return { reviewMs, progressMs };
}

/** Include lifecycle events and task edits; legacy tasks fall back to known timestamps. */
export function timeSinceLastActivity(
  task: Task,
  events: TaskActivity[],
  now: number,
): number | null {
  const timestamps = [
    task.updatedAt,
    task.reviewedAt,
    task.submittedAt,
    task.claimedAt,
    task.createdAt,
    ...events.map((event) => event.createdAt),
  ]
    .map((date) => date?.getTime())
    .filter((time): time is number => time !== undefined && Number.isFinite(time) && time > 0);
  if (!timestamps.length) return null;
  return Math.max(0, now - timestamps.reduce((latest, time) => Math.max(latest, time), 0));
}

export function buildParticipantRows({
  tasks,
  activity,
  users,
  campaignId,
  role,
  current,
  writerIds,
  now = Date.now(),
}: {
  tasks: Task[];
  activity: TaskActivity[];
  users: User[];
  campaignId: string;
  role: ParticipantRole;
  current: boolean;
  writerIds?: string[];
  now?: number;
}): ParticipantRow[] {
  const rows = new Map<string, ParticipantRow>();
  const userMap = new Map(users.map((u) => [u.uid, u]));
  const ensure = (
    id: string,
    name?: string | null,
    email?: string | null,
    expertId?: string | null,
  ) => {
    if (!rows.has(id)) {
      const user = userMap.get(id);
      rows.set(id, {
        id,
        name: user?.displayName || name || user?.email || id,
        email: user?.email || email || '',
        expertId: user?.expertId || expertId || '',
        trusted: user?.trustedCampaignIds?.includes(campaignId) === true,
        tasks: [],
        signedOff: 0,
        inReview: 0,
        averageReviewMs: null,
        averageWritingMs: null,
        revisionRatio: null,
      });
    }
    return rows.get(id)!;
  };
  users
    .filter((u) =>
      role === 'reviewers'
        ? canReviewCampaign(u, campaignId)
        : writerIds
          ? writerIds.includes(u.uid)
          : !u.isAdmin && !u.isReviewer,
    )
    .forEach((u) => ensure(u.uid));
  const byTask = new Map<string, TaskActivity[]>();
  activity.forEach((event) => {
    const entries = byTask.get(event.taskId) ?? [];
    entries.push(event);
    byTask.set(event.taskId, entries);
  });
  for (const task of tasks) {
    const events = byTask.get(task.id) ?? [];
    const people = new Set<string>();
    const assignedId = role === 'writers' ? task.ownerId : task.reviewerId;
    if (assignedId) {
      ensure(
        assignedId,
        role === 'writers' ? task.ownerName : task.reviewerName,
        role === 'writers' ? task.ownerEmail : null,
        role === 'writers' ? task.expertId : null,
      );
      if (!current || task.status !== 'archived') people.add(assignedId);
    }
    if (!current)
      events.forEach((e) => {
        const id =
          role === 'writers'
            ? ['claimed', 'submitted', 'reopened'].includes(e.kind)
              ? e.actorId
              : null
            : e.reviewerId ||
              ([
                'review_started',
                'approved',
                'changes_requested',
                'review_released',
                'escalated',
              ].includes(e.kind)
                ? e.actorId
                : null);
        if (id) {
          ensure(id, id === e.actorId ? e.actorName : null);
          people.add(id);
        }
      });
    const durations = taskDurations(task, events, now);
    const sinceLastActivityMs = timeSinceLastActivity(task, events, now);
    const revisions = Math.max(
      events.filter((e) => e.kind === 'submitted').length - 1,
      task.reviewRound - 1,
      0,
    );
    people.forEach((id) => {
      const approved =
        role === 'writers'
          ? task.status === 'approved'
          : events.some((e) => e.kind === 'approved' && e.actorId === id) ||
            (task.status === 'approved' && task.reviewerId === id);
      ensure(id).tasks.push({ task, ...durations, sinceLastActivityMs, revisions, approved });
    });
  }
  return Array.from(rows.values())
    .map((row) => {
      const reviewed = row.tasks.filter((t) => t.reviewMs > 0 || t.task.submittedAt);
      return {
        ...row,
        signedOff: row.tasks.filter((t) => t.approved).length,
        inReview: row.tasks.filter(
          (t) =>
            t.task.status === 'in_review' && (role === 'writers' || t.task.reviewerId === row.id),
        ).length,
        averageReviewMs: reviewed.length
          ? reviewed.reduce((sum, t) => sum + t.reviewMs, 0) / reviewed.length
          : null,
        averageWritingMs: row.tasks.length
          ? row.tasks.reduce((sum, t) => sum + t.progressMs, 0) / row.tasks.length
          : null,
        revisionRatio: row.tasks.length
          ? row.tasks.reduce((sum, t) => sum + t.revisions, 0) / row.tasks.length
          : null,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}
